import { getMermaid } from "./getMermaid";
import exportColors from "@design/export-colors.json";

// mermaid.render() outside the DOM emits SVGs with no explicit width/height,
// which leaves <img> rasterization with degenerate dimensions. Render into a
// hidden fixed-width container so the browser computes real layout.
async function renderMermaidSvg(
  chart: string,
): Promise<{ svg: string; width: number; height: number }> {
  const mermaid = await getMermaid();
  const container = document.createElement("div");
  container.style.cssText = "position:fixed;left:-10000px;top:0;width:900px;visibility:hidden";
  document.body.appendChild(container);
  try {
    const id = `mermaid-export-${Math.random().toString(36).slice(2, 10)}`;
    const { svg: rawSvg } = await mermaid.render(id, chart);
    container.innerHTML = rawSvg;
    const svgEl = container.querySelector("svg");
    if (!svgEl) throw new Error("Mermaid produced no SVG element");
    const rect = svgEl.getBoundingClientRect();
    const width = Math.max(Math.round(rect.width), 200);
    const height = Math.max(Math.round(rect.height), 100);
    svgEl.setAttribute("width", String(width));
    svgEl.setAttribute("height", String(height));
    svgEl.removeAttribute("style");
    return { svg: svgEl.outerHTML, width, height };
  } finally {
    document.body.removeChild(container);
  }
}

async function svgToPngDataUrl(
  svg: string,
  width: number,
  height: number,
  scale: number,
): Promise<string> {
  const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    return await new Promise<string>((resolve, reject) => {
      // Guarantee the promise settles even if the image never fires onload or
      // onerror, so a stuck rasterization can't hang the whole export.
      const timer = setTimeout(
        () => reject(new Error("Timed out rasterizing Mermaid SVG")),
        15000,
      );
      const img = new Image();
      img.onload = () => {
        clearTimeout(timer);
        try {
          const canvas = document.createElement("canvas");
          canvas.width = width * scale;
          canvas.height = height * scale;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Canvas 2d context unavailable"));
            return;
          }
          ctx.fillStyle = exportColors["image-bg"];
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/png"));
        } catch (err) {
          // drawImage / toDataURL can throw (e.g. tainted canvas); without this
          // the promise would neither resolve nor reject and the export hangs.
          reject(err);
        }
      };
      img.onerror = () => {
        clearTimeout(timer);
        reject(new Error("Failed to rasterize Mermaid SVG"));
      };
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function inlineMermaidDiagrams(
  markdown: string,
  options: { scale?: number; maxWidth?: number } = {},
): Promise<string> {
  const scale = options.scale ?? 3;
  const maxWidth = options.maxWidth ?? 450;
  const fence = /```mermaid\s*\n([\s\S]*?)```/g;
  const matches = [...markdown.matchAll(fence)];
  if (matches.length === 0) return markdown;

  const replacements: Array<{ start: number; end: number; html: string }> = [];
  for (const m of matches) {
    try {
      const { svg, width, height } = await renderMermaidSvg(m[1]);
      const dataUrl = await svgToPngDataUrl(svg, width, height, scale);
      const displayWidth = Math.min(width, maxWidth);
      const displayHeight = Math.round((height / width) * displayWidth);
      const html = `\n\n<p style="text-align:center"><img src="${dataUrl}" width="${displayWidth}" height="${displayHeight}" alt="Mermaid diagram" /></p>\n\n`;
      replacements.push({ start: m.index ?? 0, end: (m.index ?? 0) + m[0].length, html });
    } catch (err) {
      console.warn("Mermaid render failed; keeping source fence", err);
    }
  }

  let out = "";
  let cursor = 0;
  for (const r of replacements) {
    out += markdown.slice(cursor, r.start) + r.html;
    cursor = r.end;
  }
  out += markdown.slice(cursor);
  return out;
}
